import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, LinearProgress,
  Link, ListItemText, MenuItem, Stack, Switch, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import BillingTimeZoneSettings from "../components/BillingTimeZoneSettings";
import BillingWorkRules from "../components/BillingWorkRules";
import BillingNumberField from "../components/BillingNumberField";
import billingApi, { BillingSummaryResponse, CreditCategory, CreditMovement, CreditPolicy, CreditLedgerFilter } from "../apis/billingApi";
import usageApi from "../apis/usageApi";
import { useUserMsp } from "../hooks/useUserMsp";
import { formatDateTimeInTimeZone, toDateTimeLocalInTimeZone } from "../shared/utils/timezoneUtils";
import { isBillingUiVisible } from "../utils/billingAccess";
import { contractEndDate, contractMonths, formatCalendarDate } from "../utils/billingContract";
import { isHorizonRootRole } from "../utils/horizonRoles";

const credits = (units: number | string) => (Number(units) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });
const creditChange = (units: number | string) => (Number(units) > 0 ? "+" : "") + credits(units);
const newAddition = () => ({ request_id: crypto.randomUUID(), credits: 0, expires_at: "", reason: "" });
const errorText = (e: unknown) => e instanceof Error ? e.message : "Could not update billing";
const entryLabels: Record<string, string> = {
  grant: "Monthly allowance", addition: "Credits added", hold: "Reserved", release: "Released",
  capture: "Charge", reversal: "Credits returned", expire: "Expired",
};
const categoryLabels: Record<CreditCategory, string> = {
  chat: "Chat Assistant", ticket: "Tickets", workflow: "Workflows",
  allowance: "Allowance changes", addition: "Credit additions",
};
const calendarDate = (value: string) => formatCalendarDate(value, undefined, { month: "short", day: "numeric", year: "numeric" });
const cardPadding = { p: 2.5, "&:last-child": { pb: 2.5 } };
const compactFields = { display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 };
const fieldsetStyle = { border: 0, p: 0, m: 0, minWidth: 0 };
type View = "usage" | "settings" | "defaults";

export default function Billing() {
  const { userMspInfo } = useUserMsp();
  const root = isHorizonRootRole(userMspInfo?.role);
  const [params, setParams] = useSearchParams();
  const mspId = root && params.get("mspId") ? Number(params.get("mspId")) : Number(userMspInfo?.msp_id);
  const generations = useRef({ account: 0, ledger: 0 }).current;
  const loadedMsp = useRef<number | null>(null);
  const [view, setView] = useState<View>("usage");
  const global = view === "defaults";
  const [filters, setFilters] = useState<CreditLedgerFilter>({});
  const [options, setOptions] = useState<{ id: number; name: string }[]>([]);
  const [summary, setSummary] = useState<BillingSummaryResponse | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [ledger, setLedger] = useState<CreditMovement[]>([]);
  const [policy, setPolicy] = useState<CreditPolicy | null>(null);
  const [savedPolicy, setSavedPolicy] = useState<CreditPolicy | null>(null);
  const [startDate, setStartDate] = useState("");
  const [months, setMonths] = useState("12");
  const [timezone, setTimezone] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addition, setAddition] = useState(newAddition);
  const [requestOpen, setRequestOpen] = useState(false);
  const [creditRequest, setCreditRequest] = useState({ credits: 0, reason: "" });
  const [notice, setNotice] = useState<{ message: string; severity: "success" | "warning" } | null>(null);
  const policyChanged = Boolean(policy && savedPolicy && !isEqual(policy, savedPolicy));
  const endDate = contractEndDate(startDate, Number(months));
  const contractChanged = Boolean(summary && (startDate !== (summary.account?.contract_start || "")
    || endDate !== (summary.account?.contract_end || "") || timezone !== summary.timezone));
  const displayDate = (value: string) => calendarDate(toDateTimeLocalInTimeZone(value, summary!.timezone).slice(0, 10));
  const scheduled = summary?.enabled && !summary.effective;
  const scheduledMessage = summary?.account
    ? "Scheduled for " + calendarDate(summary.account.contract_start) + " (" + summary.timezone + "). Theo runs without credit limits until then."
    : "";
  const canLoadHistory = view === "usage" && Boolean(summary?.account);
  const timezoneLocked = Boolean(summary?.account
    && toDateTimeLocalInTimeZone(new Date().toISOString(), summary.timezone).slice(0, 10) < summary.account.contract_end
    && summary.periods.some(p =>
    toDateTimeLocalInTimeZone(p.starts_at, summary.timezone).slice(0, 10) >= summary.account!.contract_start));

  const load = useCallback(async () => {
    if (!mspId || !isBillingUiVisible(userMspInfo)) return;
    const generation = ++generations.account;
    setLoading(true);
    if (loadedMsp.current !== mspId) {
      loadedMsp.current = null;
      setSummary(null); setStartDate(""); setMonths("12"); setTimezone(null); setError("");
    }
    try {
      const value = await billingApi.getSummary({ mspId });
      if (generation !== generations.account) return;
      setSummary(value);
      if (loadedMsp.current !== mspId) {
        setStartDate(value.account?.contract_start || "");
        setMonths(value.account ? String(contractMonths(value.account.contract_start, value.account.contract_end) || "") : "12");
        setTimezone(value.timezone);
        setView(current => current === "usage" && root && (!value.account || !contractMonths(value.account.contract_start, value.account.contract_end)) ? "settings" : current);
        loadedMsp.current = mspId;
      }
    } catch (e) {
      if (generation === generations.account) setError(errorText(e));
    } finally { if (generation === generations.account) setLoading(false); }
  }, [mspId, root, userMspInfo?.role, userMspInfo?.credit_billing_enabled, generations]);

  const loadHistory = useCallback(async () => {
    if (!mspId || !canLoadHistory || !isBillingUiVisible(userMspInfo)) return;
    const generation = ++generations.ledger;
    try {
      const value = await billingApi.getLedger(mspId, undefined, filters);
      if (generation === generations.ledger) { setLedger(value.rows); setHasMore(value.rows.length === 100); }
    } catch (e) { if (generation === generations.ledger) setError(errorText(e)); }
  }, [mspId, canLoadHistory, userMspInfo?.role, userMspInfo?.credit_billing_enabled, filters, generations]);

  useEffect(() => { void load(); return () => { generations.account++; }; }, [load, generations]);
  useEffect(() => { const timer = setTimeout(loadHistory, 250); return () => { clearTimeout(timer); generations.ledger++; }; }, [loadHistory, generations]);
  useEffect(() => { setAddition(newAddition()); setAddOpen(false); setRequestOpen(false); setCreditRequest({ credits: 0, reason: "" }); setNotice(null); setFilters({}); setLedger([]); }, [mspId]);
  useEffect(() => {
    if (root) usageApi.getUsageMspOptionsForRoot()
      .then(rows => setOptions(rows.map(r => ({ id: Number(r.id), name: r.name || "MSP " + r.id }))))
      .catch(e => setError(errorText(e)));
  }, [root]);
  useEffect(() => {
    let live = true;
    setPolicy(null); setSavedPolicy(null);
    if (root && mspId) billingApi.getSettings(global ? 0 : mspId).then(s => {
      if (!live) return;
      const value = global ? s.defaults : s.effective;
      setPolicy(value); setSavedPolicy(value);
    }).catch(e => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [root, mspId, global]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setError("");
    try { await fn(); await Promise.all([load(), loadHistory()]); return true; }
    catch (e) { setError(errorText(e)); return false; }
    finally { setBusy(false); }
  }
  function updatePolicy<K extends keyof CreditPolicy>(key: K, value: CreditPolicy[K]) {
    if (policy) setPolicy({ ...policy, [key]: value });
  }
  function savePolicy() {
    if (!policyChanged) return;
    return act(async () => {
      const saved = await billingApi.saveSettings(global ? 0 : mspId, policy!);
      const value = global ? saved.defaults : saved.effective;
      setPolicy(value); setSavedPolicy(value);
    });
  }
  if (!isBillingUiVisible(userMspInfo)) return <Alert severity="warning">Credit usage is not available for this MSP.</Alert>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1120, width: "100%", boxSizing: "border-box", mx: "auto" }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2}>
          <Typography variant="h4">Credits and usage</Typography>
          <Stack direction="row" gap={1.5} alignItems="center">
            {root && <TextField select size="small" label="MSP" disabled={busy || global}
              sx={{ width: { xs: "100%", sm: 280 }, minWidth: 0 }}
              value={options.some(o => o.id === mspId) ? mspId : ""} onChange={e => setParams({ mspId: e.target.value })}>
              {options.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextField>}
            <Button variant="outlined" size="small" disabled={busy || loading} sx={{ flexShrink: 0 }} onClick={() => { setError(""); void load(); void loadHistory(); }}>Refresh</Button>
          </Stack>
        </Stack>
        {loading && <LinearProgress aria-label="Loading billing" />}
        {!global && summary && <Card component="section" aria-labelledby="credit-billing-heading" variant="outlined"><CardContent sx={cardPadding}>
          <Stack spacing={2.5}>
            <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
              <Box sx={{ minWidth: 0 }}>
                <Typography id="credit-billing-heading" variant="h6">Credit billing</Typography>
                {(!root || scheduled || !summary.enabled) && <Typography variant="body2" color="text.secondary">
                  {scheduled ? scheduledMessage : summary.enabled && summary.account ? "Contract " + calendarDate(summary.account.contract_start) + " – " + calendarDate(summary.account.contract_end) + " · " + summary.timezone : summary.account ? "Off. Theo runs without credit limits." : "Off. Set up a contract to get started."}
                </Typography>}
                {root && summary.activation && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {summary.activation.already_issued ? "This month's allowance has already been issued." : "Monthly allowance: " + credits(summary.activation.grant_units) + " credits · " + displayDate(summary.activation.starts_at) + " – " + displayDate(summary.activation.ends_at)}
                </Typography>}
              </Box>
              {root && <FormControlLabel sx={{ m: 0, flexShrink: 0 }} label={scheduled ? "Scheduled" : summary.enabled ? "On" : "Off"}
                control={<Switch inputProps={{ role: "switch", "aria-label": "Credit billing for this MSP" }}
                  checked={summary.enabled} disabled={busy || loading || (!summary.enabled && !summary.activation)}
                  onChange={(_, enabled) => { void act(() => billingApi.setEnabled(mspId, enabled)); }} />} />}
            </Stack>
            {root && <>
              <Divider />
              <Box component="form" aria-label="Billing contract" onSubmit={event => {
                event.preventDefault();
                if (!endDate || !timezone || !contractChanged || !event.currentTarget.reportValidity()) return;
                void act(() => billingApi.saveAccount(mspId, { contract_start: startDate, contract_end: endDate, timezone }));
              }}>
                <Box component="fieldset" disabled={loading || busy} sx={fieldsetStyle}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "280px minmax(0, 1fr)", md: "280px minmax(0, 360px) auto" }, gap: 2, alignItems: "start" }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 96px", gap: 2 }}>
                        <TextField size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} />
                        <TextField size="small" type="number" label="Months" inputProps={{ min: 1, step: 1 }} value={months} onChange={e => setMonths(e.target.value)} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{endDate ? "Ends " + calendarDate(endDate) + " · Credits issued monthly" : "Start on any date. Choose how many months the contract runs."}</Typography>
                    </Stack>
                    <BillingTimeZoneSettings value={timezone} onChange={setTimezone} locked={timezoneLocked} />
                    <Button type="submit" variant="outlined" size="small" sx={{ justifySelf: "start", minHeight: 40 }} disabled={!endDate || !timezone || !contractChanged || loading || busy}>Save contract</Button>
                  </Box>
                </Box>
              </Box>
            </>}
          </Stack>
        </CardContent></Card>}
        {root && <Tabs value={view} onChange={(_, value) => { setView(value); setError(""); }} aria-label="Billing sections"
          sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }} variant="scrollable" scrollButtons="auto">
          <Tab value="usage" label="Usage" /><Tab value="settings" label="Settings" /><Tab value="defaults" label="Shared defaults" />
        </Tabs>}
        {error && !addOpen && !requestOpen && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert>}

        {view === "usage" && (summary?.account ? <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6">Credit balance</Typography>
              <Typography variant="body2" color="text.secondary">
                All managed clients share this credit pool. {summary.period && displayDate(summary.period.starts_at) + " – " + displayDate(summary.period.ends_at)}
              </Typography>
            </Box>
            {root && <Button size="small" variant="outlined" disabled={busy || !summary.effective} sx={{ flexShrink: 0 }}
              onClick={() => { setError(""); setAddOpen(true); }}>Add credits</Button>}
            {!root && <Button size="small" variant="outlined" disabled={busy || loading || !summary.enabled} sx={{ flexShrink: 0 }}
              onClick={() => { setError(""); setNotice(null); setRequestOpen(true); }}>Request credits</Button>}
          </Stack>
          {summary.effective && Number(summary.balances.available_units) + Number(summary.balances.held_units) === 0 &&
            <Alert severity="warning">Credits exhausted. AI work is paused.{!root && " Contact the Theo team."}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
            {[["Available", summary.balances.available_units], ["Reserved", summary.balances.held_units],
              ["Used this month", summary.period?.used_units || 0], ["Budget this month", summary.period?.budget_units || 0]].map(([label, units]) =>
              <Card key={label} variant="outlined"><CardContent sx={cardPadding}>
                <Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="h4" sx={{ mt: 1 }}>{credits(units)}</Typography>
              </CardContent></Card>)}
          </Box>
          <Card variant="outlined"><CardContent sx={cardPadding}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">Allowance and expiry</Typography>
                {summary.policy.ramp.map(p => <Typography key={p.month} variant="body2">From month {p.month}: {p.credits.toLocaleString()} credits per month</Typography>)}
                <Typography variant="body2" color="text.secondary">{summary.policy.rollover === "term" ? "Unused credits carry through the contract." : "Unused credits expire " + summary.policy.rollover_days + (summary.policy.rollover_days === 1 ? " day" : " days") + " after their month ends."} All credits expire by contract end.</Typography>
                {summary.period && <Typography variant="body2">Next allocation: {toDateTimeLocalInTimeZone(summary.period.ends_at, summary.timezone).slice(0, 10) >= summary.account.contract_end ? "Contract ends" : displayDate(summary.period.ends_at)}</Typography>}
                {summary.grants.map(g => <Typography key={g.id} variant="body2" color="text.secondary">{credits(g.available_units)} available · expires {displayDate(g.expires_at)}</Typography>)}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle1">Usage by client</Typography>
                {summary.clients.length ? summary.clients.map(r => <Typography key={String(r.customer_id) + r.kind} variant="body2">{r.name || "MSP"} · {r.kind}: {credits(r.units)} credits</Typography>)
                  : <Typography variant="body2" color="text.secondary">No usage yet.</Typography>}
                {summary.breakdown.map(r => <Typography key={r.kind + r.origin} variant="body2" color="text.secondary">{r.kind === "ticket" ? r.origin + " tickets" : r.kind}: {r.completed} completed · {credits(r.units)} credits</Typography>)}
              </Stack>
            </Box>
          </CardContent></Card>
          <Card variant="outlined"><CardContent sx={cardPadding}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="h6">Credit history</Typography>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => act(async () => {
                  const blob = await billingApi.exportLedger(mspId, filters);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = "theo-credit-ledger.csv"; a.click(); URL.revokeObjectURL(url);
                })}>Export CSV</Button>
              </Stack>
              <Box sx={compactFields}>
                <TextField size="small" label="Search" placeholder="Ticket, workflow or client" sx={{ width: { xs: "100%", sm: 260 } }} value={filters.search || ""} onChange={e => setFilters({ ...filters, search: e.target.value })} />
                <TextField size="small" select label="Month" sx={{ width: { xs: "100%", sm: 220 } }} value={filters.period_id || ""} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} onChange={e => setFilters({ ...filters, period_id: e.target.value })}>
                  <MenuItem value="">All months</MenuItem>{summary.periods.map(p => <MenuItem key={p.id} value={String(p.id)}>{displayDate(p.starts_at)} – {displayDate(p.ends_at)}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Category" sx={{ width: { xs: "100%", sm: 260 } }} value={filters.categories || []} InputLabelProps={{ shrink: true }} SelectProps={{
                  multiple: true, displayEmpty: true,
                  renderValue: value => {
                    const selected = value as CreditCategory[];
                    return selected.length === 0 ? "All categories" : selected.length === 1 ? categoryLabels[selected[0]] : `${selected.length} categories`;
                  },
                }} onChange={e => {
                  const selected = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value as string[];
                  setFilters({ ...filters, categories: selected.includes("") ? [] : selected as CreditCategory[] });
                }}>
                  <MenuItem value=""><Checkbox checked={!filters.categories?.length} /><ListItemText primary="All categories" /></MenuItem>
                  {(Object.keys(categoryLabels) as CreditCategory[]).map(c => <MenuItem key={c} value={c}>
                    <Checkbox checked={filters.categories?.includes(c) || false} /><ListItemText primary={categoryLabels[c]} />
                  </MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{ overflowX: "auto" }}><Table size="small" sx={{ minWidth: 900 }} aria-label="Credit history"><TableHead><TableRow>
                {["Date", "Entry", "Client", "Work", "Available change", "Reserved change", "Reason"].map(h => <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>{h}</TableCell>)}
              </TableRow></TableHead><TableBody>
                {ledger.map(r => <TableRow key={r.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTimeInTimeZone(r.created_at, summary.timezone)}</TableCell><TableCell>{entryLabels[r.kind]}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.work_kind === "ticket" ? r.origin === "automatic" ? "Automatic ticket" : "Technician ticket" : r.work_kind === "chat" ? "Chat Assistant" : r.work_kind === "workflow" ? "Workflow" : ""}</Typography>
                    {r.ticket_id ? <Link href={"/supervisor/tickets/" + encodeURIComponent(r.ticket_id)}>{r.ticket_id}</Link> : r.ai_thread_id ? <Link href={"/chat-assistant/" + r.ai_thread_id}>Conversation</Link> : ""}
                  </TableCell>
                  <TableCell>{creditChange(r.available_delta)}</TableCell><TableCell>{creditChange(r.held_delta)}</TableCell><TableCell>{r.reason}</TableCell>
                </TableRow>)}
                {!ledger.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>No credit activity matches these filters.</TableCell></TableRow>}
              </TableBody></Table></Box>
              {ledger.length > 0 && hasMore && <Button disabled={busy} sx={{ alignSelf: "flex-start" }} onClick={async () => {
                setBusy(true);
                try { const generation = generations.ledger; const next = await billingApi.getLedger(mspId, ledger[ledger.length - 1].id, filters); if (generation === generations.ledger) { setLedger(current => [...current, ...next.rows]); setHasMore(next.rows.length === 100); } }
                catch (e) { setError(errorText(e)); } finally { setBusy(false); }
              }}>Load older entries</Button>}
            </Stack>
          </CardContent></Card>
        </> : !loading && <Typography color="text.secondary">No credit activity yet.</Typography>)}

        {root && view !== "usage" && <Card variant="outlined"><CardContent sx={cardPadding}>
          <Box component="form" onSubmit={event => {
            event.preventDefault();
            if (event.currentTarget.reportValidity()) void savePolicy();
          }}>
          <Box component="fieldset" disabled={loading || busy} sx={fieldsetStyle}>
            <Stack spacing={2.5}>
              <Typography variant="h6">{global ? "Shared defaults" : "Credit settings"}</Typography>
              {global && <Alert severity="info">Changes apply to MSPs using these defaults.</Alert>}
              {policy && <>
                <FormControlLabel sx={{ m: 0 }} label="Bill ROOT usage" control={<Switch checked={policy.count_root_usage}
                  onChange={(_, checked) => updatePolicy("count_root_usage", checked)} />} />
                <Typography variant="body2" color="text.secondary">Charge credits for new work started by ROOT users. Automatic work and work started by other users stay billable.</Typography>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Monthly allowance</Typography>
                  <Typography variant="body2" color="text.secondary">{global ? "Applies to future monthly allocations." : "Applies from the selected month until the next allowance change. If that includes the current month, its issued allowance is updated. Used or reserved credits cannot be removed."}</Typography>
                  {policy.ramp.map((p, i) => <Box key={i} sx={compactFields}>
                    <BillingNumberField size="small" label="From month" inputProps={{ min: 1, readOnly: i === 0 }} sx={{ width: 100 }} value={p.month} onChange={month => updatePolicy("ramp", policy.ramp.map((r, n) => n === i ? { ...r, month } : r))} />
                    <BillingNumberField size="small" label="Credits per month" inputProps={{ min: 0, step: 0.1 }} sx={{ width: 170 }} value={p.credits} onChange={value => updatePolicy("ramp", policy.ramp.map((r, n) => n === i ? { ...r, credits: value } : r))} />
                    {i > 0 && <Button size="small" onClick={() => updatePolicy("ramp", policy.ramp.filter((_, n) => n !== i))}>Remove</Button>}
                  </Box>)}
                  <Button size="small" sx={{ alignSelf: "flex-start" }} onClick={() => updatePolicy("ramp", [...policy.ramp, { month: (policy.ramp[policy.ramp.length - 1]?.month || 0) + 1, credits: 0 }])}>Add allowance change</Button>
                </Stack>
                <Divider />
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Credit rates</Typography>
                  <Box sx={compactFields}>
                    {(["chat_rate", "automatic_ticket_rate", "technician_ticket_rate"] as const).map(k => <BillingNumberField size="small" key={k} inputProps={{ min: 0.1, step: 0.1 }} sx={{ width: { xs: "100%", sm: 200 } }} label={{ chat_rate: "Per chat message", automatic_ticket_rate: "Automatic ticket", technician_ticket_rate: "Technician ticket" }[k]} value={policy[k]} onChange={value => updatePolicy(k, value)} />)}
                  </Box>
                </Stack>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Unused credits</Typography>
                  <Box sx={compactFields}>
                    <TextField size="small" select label="Expire" sx={{ width: { xs: "100%", sm: 270 } }} value={policy.rollover} onChange={e => updatePolicy("rollover", e.target.value as CreditPolicy["rollover"])}>
                      <MenuItem value="days">After the subscription month</MenuItem><MenuItem value="term">At contract end</MenuItem>
                    </TextField>
                    {policy.rollover === "days" && <BillingNumberField size="small" label="Days later" sx={{ width: 130 }} inputProps={{ min: 0 }} value={policy.rollover_days} onChange={value => updatePolicy("rollover_days", value)} />}
                  </Box>
                </Stack>
                {!global && <><Divider /><BillingWorkRules mspId={mspId} rates={policy.workflow_rates} completedStatuses={policy.completed_statuses}
                  onRatesChange={rates => updatePolicy("workflow_rates", rates)}
                  onStatusesChange={statuses => updatePolicy("completed_statuses", statuses)} disabled={loading || busy} /></>}
                <Stack direction="row" flexWrap="wrap" gap={1.5}>
                  <Button variant="contained" size="small" type="submit" disabled={busy || loading || !policyChanged}>Save {global ? "shared defaults" : "credit settings"}</Button>
                </Stack>
              </>}
            </Stack>
          </Box>
          </Box>
        </CardContent></Card>}
      </Stack>
      {root && <Dialog open={addOpen} onClose={() => { if (!busy) setAddOpen(false); }} maxWidth="sm" fullWidth
        PaperProps={{ component: "form", onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (!event.currentTarget.reportValidity()) return;
          if (await act(() => billingApi.addCredits(mspId, addition))) { setAddition(newAddition()); setAddOpen(false); }
        } }}>
        <DialogTitle>Add approved credits</DialogTitle>
        <DialogContent><Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">Add credits approved by the purchasing contact. This updates the balance immediately.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <BillingNumberField size="small" label="Credits" inputProps={{ min: 0.1, step: 0.1 }} value={addition.credits} onChange={value => setAddition({ ...addition, credits: value })} />
            <TextField size="small" type="date" label="Expires on" InputLabelProps={{ shrink: true }} value={addition.expires_at} onChange={e => setAddition({ ...addition, expires_at: e.target.value })} />
          </Box>
          <TextField size="small" fullWidth label="Approval reference and reason" value={addition.reason} onChange={e => setAddition({ ...addition, reason: e.target.value })} />
        </Stack></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button disabled={busy} onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={busy || addition.credits <= 0 || !addition.expires_at || !addition.reason.trim()}>Add credits</Button>
        </DialogActions>
      </Dialog>}
      {!root && <Dialog open={requestOpen} onClose={() => { if (!busy) setRequestOpen(false); }} maxWidth="sm" fullWidth
        PaperProps={{ component: "form", onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (!event.currentTarget.reportValidity()) return;
          setBusy(true); setError("");
          try {
            const result = await billingApi.requestCredits(mspId, creditRequest);
            setRequestOpen(false); setCreditRequest({ credits: 0, reason: "" });
            setNotice(result.email_sent
              ? { message: "Request sent to the Theo team. Credits will be added after approval.", severity: "success" }
              : { message: "Request saved in Theo, but email delivery failed. The team can still review it in notifications.", severity: "warning" });
          } catch (e) { setError(errorText(e)); }
          finally { setBusy(false); }
        } }}>
        <DialogTitle>Request credits</DialogTitle>
        <DialogContent><Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">Request a one-time addition to your balance, subject to approval. Your monthly allowance stays the same.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <BillingNumberField size="small" label="Additional credits" disabled={busy} inputProps={{ min: 0.1, step: 0.1 }} value={creditRequest.credits}
            onChange={credits => setCreditRequest({ ...creditRequest, credits })} />
          <TextField size="small" label="Reason" multiline minRows={2} disabled={busy} inputProps={{ required: true, maxLength: 1000 }} value={creditRequest.reason}
            onChange={e => setCreditRequest({ ...creditRequest, reason: e.target.value })} />
        </Stack></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button disabled={busy} onClick={() => setRequestOpen(false)}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={busy || creditRequest.credits <= 0 || !creditRequest.reason.trim()}>Send request</Button>
        </DialogActions>
      </Dialog>}
    </Box>
  );
}
